import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { SavedArtworkPageModel, SavedArtworkPageRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['savedArtworkPages']

type SavedArtworkPageFilters = {
  agency_id?: number,
  string?: string,
  user_id?: number,
}

type UseSavedArtworkPagesOptions = {
  filters?: SavedArtworkPageFilters,
  performHttpRequests?: boolean,
  requestOptions?: SavedArtworkPageRequestOptions,
}

function useSavedArtworkPages(options: UseSavedArtworkPagesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    string: filterString,
    user_id: filterUserId,
  } = filters

  const {
    updatedEntities: { savedArtworkPages: savedArtworkPagesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { savedArtworkPages } = useSelector(reduxState => reduxState.entities)

  const filteredSavedArtworkPages = useMemo(() => {
    const filtered = Object.values(savedArtworkPages).filter((savedArtworkPage: SavedArtworkPageModel) => {
      const {
        agency_id, id, title, user_id,
      } = savedArtworkPage

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)

      const matchUserId = matchFilterNumber(user_id, filterUserId)
      const matchString = matchId || matchTitle

      return matchAgencyId && matchString && matchUserId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [savedArtworkPagesUpdatedAt, JSON.stringify(filters)])

  const filteredSavedArtworkPagesCount = filteredSavedArtworkPages.length
  const hasFilteredSavedArtworkPages = !!filteredSavedArtworkPagesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredSavedArtworkPagesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingSavedArtworkPages } = useReduxAction(
    'savedArtworkPages',
    'loadSavedArtworkPages',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredSavedArtworkPages,
    filteredSavedArtworkPagesCount,
    hasFilteredSavedArtworkPages,
    loading: loadingSavedArtworkPages,
  }
}

export default useSavedArtworkPages
